import { request } from "$/lib/api"
import partQueries from "$/lib/queries/part-queries"
import { Route, ServiceBikeType, populateRoute } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchSupportedBikeTypes(
  storeId: string,
): Promise<ServiceBikeType[]> {
  return await request<ServiceBikeType[]>({
    url: populateRoute(Route.SUPPORTED_BIKE_TYPE__FIND_ALL, storeId),
    method: "GET",
  })
}

async function addSupportedBikeType(
  storeId: string,
  bikeType: ServiceBikeType,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.SUPPORTED_BIKE_TYPE__ADD, storeId),
    method: "POST",
    data: { bikeType },
  })
}

async function removeSupportedBikeType(
  storeId: string,
  bikeType: ServiceBikeType,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.SUPPORTED_BIKE_TYPE__DELETE, storeId, bikeType),
    method: "DELETE",
  })
}

export const useSupportedBikeTypeMutations = (storeId: string) => {
  const queryClient = useQueryClient()

  // The parts a store sees depend on its supported types
  const invalidateQueries = async () =>
    await Promise.all([
      queryClient.invalidateQueries(supportedBikeTypeQueries.list(storeId)),
      queryClient.invalidateQueries({ queryKey: partQueries.lists() }),
    ])

  const addMutation = useMutation({
    mutationFn: (bikeType: ServiceBikeType) =>
      addSupportedBikeType(storeId, bikeType),
    onSettled: invalidateQueries,
  })

  const removeMutation = useMutation({
    mutationFn: (bikeType: ServiceBikeType) =>
      removeSupportedBikeType(storeId, bikeType),
    onSettled: invalidateQueries,
  })

  return { addMutation, removeMutation }
}

const supportedBikeTypeQueries = {
  all: () => ["supportedBikeType"],
  lists: () => [...supportedBikeTypeQueries.all(), "list"],
  list: (storeId: string) =>
    queryOptions({
      queryKey: [...supportedBikeTypeQueries.lists(), storeId],
      queryFn: () => fetchSupportedBikeTypes(storeId),
    }),
}

export default supportedBikeTypeQueries
